<script lang="ts" setup>


import {useUser} from "~/composables/useUser";
import * as yup from "yup";
import {useForm} from "vee-validate";
import {validatePassword} from "~/utils/util";

type Step = 'submitEmail' | 'changePassword' | 'success';

const actualStep = useState<Step>('actualStep', () => 'submitEmail');
const timer = useState<number>('timer', () => 59);
const {openModal} = useModal();
const {validateTokenForPasswordReset, generateTokenForPasswordReset} = useUser()

const userEmail = ref('')
let timerIntervalId: NodeJS.Timeout | null = null;

const validationSchema = computed(() => {
  if (actualStep.value === 'submitEmail') {
    return yup.object({
      email: yup.string()
          .email('Informe um e-mail válido')
          .required('E-mail é obrigatório'),
    });
  }

  if (actualStep.value === 'changePassword') {
    return yup.object({
      token: yup.string().required('Token é obrigatório'),
      password: yup.string()
          .required('Senha é obrigatória')
          .min(8, 'A senha deve ter pelo menos 8 caracteres')
          .matches(/^(?=.*[A-Za-z])(?=.*\d)/, 'A senha deve conter pelo menos uma letra e um número')
          .test('no-sequence', 'A senha não pode conter sequências simples (ex: 123 ou abc)', validatePassword),
      confirmPassword: yup.string()
          .required('A confirmação de senha é obrigatória')
          .oneOf([yup.ref('password'), ''], 'As senhas não conferem'),
    });
  }

  return yup.object({});
});

const {handleSubmit} = useForm({validationSchema: validationSchema});

const onSubmit = handleSubmit(async (values) => {
  console.log(values)
  if (actualStep.value === 'submitEmail') {
    await submitEmail(values.email.trim().toLowerCase());
  } else if (actualStep.value === 'changePassword') {
    await submitPassword(values.password.trim(), values.token, userEmail.value);
  }
});

const submitEmail = async (email: string) => {
  userEmail.value = email
  const generated = await generateTokenForPasswordReset(email.toLowerCase().trim());
  if (!generated) {
    Alert.error("Erro ao enviar o email de recuperação");
    return;
  }
  actualStep.value = 'changePassword';
  startTimer();
}

const submitPassword = async (password: string, token:string, email:string) => {
  const exec = await validateTokenForPasswordReset(email, token, password.trim());
  actualStep.value = exec ? 'success' : 'changePassword';
}

const resendEmail = async () => {
  if (timer.value <= 0) {
    await submitEmail(userEmail.value);
  }
}

function startTimer() {
  timer.value = 10;
  timerIntervalId = setInterval(() => {
    if (timer.value > 0) timer.value--;
    else stopInterval();
  }, 1000);
}

function stopInterval() {
  if (timerIntervalId) {
    clearInterval(timerIntervalId);
    timerIntervalId = null;
  }
}

const showModal = () => {
  openModal('sign-modal')
  actualStep.value = 'submitEmail'
}

const switchToSignIn = () => {
  actualStep.value = 'submitEmail'
  openModal('sign-modal')
}

</script>
<template>
  <div class="row mx-0 align-items-center">
    <div class="px-4 pt-2 pb-4 px-sm-5 pb-sm-5 pt-md-5">
      <div v-if="actualStep === 'submitEmail'">
        <div class="mb-4">
          <h2 class="h3 mb-2">Recuperar senha</h2>
          <p>Insira seu e-mail para recuperar sua senha</p>
        </div>
        <form @submit.prevent="onSubmit">
          <DynamicForm :fields="[{ name: 'email', type: 'email', label: 'E-mail' }]"/>
          <div class="mt-3">
            <button type="submit" class="btn btn-primary px-5">Confirmar</button>
          </div>
        </form>
        <div class="mt-sm-2 pt-md-3">
          Lembra da sua senha?
          <a class="text-primary" style="cursor: pointer" @click="showModal">Entrar</a>
        </div>
      </div>
      <div v-else-if="actualStep === 'changePassword'">
        <div class="mb-4">
          <h2 class="h3 mb-2">Criar nova senha</h2>
          <p>Informe uma nova senha para sua conta</p>
        </div>
        <form @submit.prevent="onSubmit">
          <DynamicForm :fields="[
              { name: 'token', type: 'text', label: 'Token' },
              { name: 'password', type: 'password', label: 'Senha' },
              { name: 'confirmPassword', type: 'password', label: 'Confirmar senha' }
            ]"/>
          <div class="mt-3">
            <button type="submit" class="btn btn-primary px-5">Confirmar</button>
          </div>
        </form>
        <div class="mt-sm-2 pt-md-3">
          Não recebeu o e-mail?
          <button class="border-0 bg-transparent" :class="{'text-primary': timer <= 0}" :disabled="timer > 0"
                  style="cursor: pointer" @click="resendEmail">Reenviar
          </button>
          {{ (timer > 0) ? `00:${String(timer).padStart(2, '0')}` : '' }}
        </div>
      </div>
      <div v-else-if="actualStep === 'success'">
        <h2 class="h3 mb-2">Senha redefinida com sucesso!</h2>
        <p>Você já pode acessar sua conta com a nova senha.</p>
        <button class="btn btn-primary" @click="switchToSignIn">Entrar</button>
      </div>
    </div>
  </div>
</template>

<style>

</style>
